import { Link } from "react-router-dom";
import { ArrowRight, Users, Clock, DollarSign, Frame, Palette, Check, Sparkles, School, PartyPopper, Building2, Feather, BookOpen, Layers } from "lucide-react";
import PaymentBadges from "@/components/PaymentBadges";

const WORKSHOP_HERO = "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/o6yqo32s_PSX_20240121_144102.jpg";
const KIDS_CANVAS_IMG = "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/awoanzgr_PSX_20231229_093308.jpg";
const CAM_LOGO_IMG = "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/vud4u3ud_Screenshot_20260409_124751_Adobe%20Photoshop%20Express%281%29.jpg";

const AUDIENCE = [
  { icon: School, label: "Schools" },
  { icon: PartyPopper, label: "Parties & events" },
  { icon: Users, label: "Community groups" },
  { icon: Building2, label: "Corporate teams" },
];

const INCLUDED = [
  { icon: Frame, title: "Professionally framed canvas", desc: "Each student takes home a stretched & framed 30cm × 30cm canvas of their own painting." },
  { icon: Palette, title: "New set of acrylic paints", desc: "Fresh brushes + acrylic paints go home with every student — theirs to keep and keep painting with." },
  { icon: BookOpen, title: "Dreamtime storytelling", desc: "Students listen to stories passed down generation to generation while Cam paints in front of them." },
  { icon: Layers, title: "Symbol & meaning breakdown", desc: "Kids learn the meanings of aboriginal symbols and how they carry story, place and family." },
  { icon: Feather, title: "A group piece for the school", desc: "Cam paints a 1m × 2m group canvas incorporating ideas chosen by the students — stretched, framed & hung in the classroom next day." },
  { icon: Sparkles, title: "Cultural discussion + display", desc: "Authentic CKA artworks travel with Cam so students can see the range of styles, colours and stories represented in the culture." },
];

const AT_A_GLANCE = [
  { icon: Clock, label: "Run time", value: "10 hours · over 2 days" },
  { icon: Users, label: "Max students per group", value: "25" },
  { icon: DollarSign, label: "Cost per student", value: "$180 AUD" },
  { icon: Feather, label: "Presented by", value: "Cameron Boyle · Proud Barkindji man" },
];

export default function WorkshopService() {
  return (
    <div data-testid="page-workshop-service" className="text-white">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={WORKSHOP_HERO} alt="CKA cultural arts workshop" className="w-full h-full object-cover opacity-[0.14] blur-sm" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/95 via-[#0A0A0A]/90 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-20">
          <div className="inline-flex items-center gap-2 border border-[#FF4500]/50 bg-[#FF4500]/[0.08] text-[#FF4500] text-[10px] uppercase tracking-widest px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF4500] rounded-full animate-pulse" /> Workshops for Schools · Parties · Community groups
          </div>
          <p className="overline mb-5" data-testid="workshop-eyebrow">The Studio · Cultural Arts Workshop</p>
          <h1 data-testid="workshop-title" className="font-display text-5xl sm:text-7xl md:text-[8rem] leading-[0.85] max-w-5xl">
            Cultural Arts <br /><span className="text-[#FF4500]">& Aboriginal</span><br />learning experience.
          </h1>
          <p className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
            A hands-on 2-day workshop where kids learn the meaning of aboriginal symbols, listen to Dreamtime stories, watch Cam paint a school-scale group piece, and paint their own framed canvas to take home.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/custom?type=workshop" data-testid="workshop-cta-book" className="btn-primary">
              Book a workshop <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#included" className="btn-ghost">What&rsquo;s included</a>
            <a href="#pricing" className="btn-ghost">Pricing →</a>
          </div>
        </div>
      </section>

      {/* THE STORY */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-16 items-start">
        <div>
          <p className="overline mb-3">The experience</p>
          <h2 className="font-display text-4xl md:text-6xl mb-8">Story first. <span className="text-[#FF4500]">Then a brush.</span></h2>
          <div className="space-y-5 text-white/70 leading-relaxed">
            <p>
              Kids start by learning the meanings of aboriginal symbols and what they stand for. They watch as a painting is created in front of them that weaves in ideas <em>they&rsquo;ve chosen as a group</em> — a piece that represents the current moment the class is sharing together.
            </p>
            <p>
              They listen to Dreamtime stories passed down generation to generation. Authentic CKA artworks are on display so they can see indigenous culture represented in many different styles, colours and stories — not one voice, but a chorus.
            </p>
            <p>
              Once the group piece is done, students paint their <strong className="text-white">own canvas</strong>. Every kid&rsquo;s work is stretched, framed and ready to hang in the classroom the next day — and their personal canvas goes home with them, hopefully to be enjoyed for generations to come.
            </p>
          </div>

          <div className="mt-10 border border-white/10 bg-[#141414] p-6">
            <p className="overline mb-3">Presented by</p>
            <p className="font-display text-2xl md:text-3xl">Cameron Boyle <span className="text-white/50 text-lg md:text-xl">· Proud Barkindji man · Indigenous artist</span></p>
            <p className="text-sm text-white/60 mt-2">ABN 21 390 573 495 · Cam&rsquo;s Koori Art Craft</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {AT_A_GLANCE.map((s) => (
            <div key={s.label} data-testid={`workshop-at-glance-${s.label.replace(/\s+/g, "-").toLowerCase()}`} className="border border-white/10 bg-[#141414] p-5 flex items-center gap-4">
              <div className="w-11 h-11 border border-[#FF4500]/40 bg-[#FF4500]/[0.08] flex items-center justify-center shrink-0">
                <s.icon className="w-5 h-5 text-[#FF4500]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-white/50">{s.label}</p>
                <p className="font-display text-lg leading-tight mt-0.5">{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* THE 2-DAY FLOW */}
      <section className="border-y border-white/10 bg-gradient-to-b from-[#141414] via-[#0F0F0F] to-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-3">How it runs</p>
            <h2 className="font-display text-4xl md:text-6xl mb-4">10 hours. 2 days.</h2>
            <p className="text-white/60">A relaxed, immersive format — half of it Cam painting & storytelling, half of it the students painting their own piece.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* DAY 1 */}
            <div data-testid="workshop-day-1" className="border border-white/10 bg-[#141414] p-6 md:p-8 hover:border-[#FF4500] transition-colors">
              <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold mb-2">Day 1</p>
              <h3 className="font-display text-3xl md:text-4xl mb-6">Story, symbol & the group piece.</h3>
              <ul className="space-y-4 text-white/75">
                {[
                  "Welcome + intro to Barkindji culture and CKA",
                  "Learn the meanings of aboriginal symbols",
                  "Students pick ideas that represent the group",
                  "Cam paints the 1m × 2m group canvas in front of them",
                  "Dreamtime storytelling throughout the paint",
                  "Group piece taken away for professional stretching & framing overnight",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm">
                    <Check className="w-4 h-4 text-[#FF4500] mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* DAY 2 */}
            <div data-testid="workshop-day-2" className="border border-white/10 bg-[#141414] p-6 md:p-8 hover:border-[#FF4500] transition-colors">
              <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold mb-2">Day 2</p>
              <h3 className="font-display text-3xl md:text-4xl mb-6">Your canvas, your story.</h3>
              <ul className="space-y-4 text-white/75">
                {[
                  "The framed group piece is hung in the classroom",
                  "Each student receives their own 30cm × 30cm stretched canvas",
                  "New brushes & acrylic paints handed out to keep",
                  "Cam supports each student to find their own symbols & story",
                  "Students paint their personal canvas",
                  "Finished canvases fixed into frames · students take them home",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm">
                    <Check className="w-4 h-4 text-[#FF4500] mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED PER STUDENT */}
      <section id="included" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="overline mb-3">Every student gets</p>
            <h2 className="font-display text-4xl md:text-6xl">Take-home <span className="text-[#FF4500]">for keeps.</span></h2>
          </div>
          <p className="text-white/50 text-sm max-w-md">Not just an experience — every kid leaves with a framed piece of their own story on canvas.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {INCLUDED.map((k) => (
            <div key={k.title} data-testid={`workshop-included-${k.title.split(" ")[0].toLowerCase()}`} className="border border-white/10 bg-[#141414] p-6 hover:border-[#FF4500]/60 transition-colors">
              <div className="w-10 h-10 border border-[#FF4500]/40 bg-[#FF4500]/[0.08] flex items-center justify-center mb-4">
                <k.icon className="w-5 h-5 text-[#FF4500]" />
              </div>
              <h3 className="font-display text-xl tracking-wider leading-tight mb-2">{k.title}</h3>
              <p className="text-xs text-white/60 leading-relaxed">{k.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-y border-white/10 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-10 items-center">
            <div>
              <p className="overline mb-3">Who it&rsquo;s for</p>
              <h2 className="font-display text-3xl md:text-5xl">Made to travel.</h2>
              <p className="text-white/60 mt-3 max-w-md">The workshop runs anywhere with a room, tables, and 25 keen kids. Cam brings everything else.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {AUDIENCE.map((a) => (
                <div key={a.label} className="border border-white/10 bg-[#141414] p-5 text-center hover:border-[#FF4500] transition-colors">
                  <a.icon className="w-6 h-6 text-[#FF4500] mx-auto mb-3" />
                  <p className="font-display text-sm md:text-base tracking-wider">{a.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 text-center">
        <p className="overline mb-3">Straight pricing</p>
        <h2 className="font-display text-4xl md:text-6xl mb-6">$180 per student.</h2>
        <p className="text-white/60 max-w-2xl mx-auto mb-10">
          Includes framed canvas, brushes, acrylic paints, materials, storytelling, group piece, and everything Cam brings on the day. Max 25 students per group; multiple groups can be booked back-to-back for larger cohorts.
        </p>
        <div className="max-w-3xl mx-auto border border-[#FF4500]/40 bg-[#FF4500]/[0.04] p-8 md:p-10 mb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-left">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Per student</p>
              <p className="font-display text-3xl text-[#FF4500]">$180</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Group size</p>
              <p className="font-display text-3xl">25 max</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Runtime</p>
              <p className="font-display text-3xl">10 hrs</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Days</p>
              <p className="font-display text-3xl">2</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/custom?type=workshop" data-testid="workshop-cta-book-2" className="btn-primary inline-flex">
            Book a workshop <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" className="btn-ghost inline-flex">
            Ask Cam a question
          </Link>
        </div>
      </section>

      {/* PAYMENT BADGES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <PaymentBadges />
      </section>
    </div>
  );
}
